/**
 * Ecommerce analytics payload builders — PURE (no DOM, no DB, no network) so
 * they are unit-tested in tests/unit/analytics-ecommerce.test.ts.
 *
 * One builder per funnel step; each returns a {@link TrackPayload} carrying
 * the GA4 event and, where Meta has a standard equivalent, the Meta Pixel
 * event. `track()` in ./client.ts sends each half only under its own consent
 * (analytics → GA4, marketing → Meta).
 *
 * Naming rules (so GA4 reports don't split one room into three):
 *  - item_id  = room slug (stable across locales)
 *  - item_name / item_category2 / item_variant = ENGLISH room / room-type /
 *    rate-plan names, never the visitor's locale
 *  - only GA4 standard fields for items; the booking-level extras (nights,
 *    lead_time_days, adults, children, payment_type) become custom dimensions
 *    once registered in GA4 Admin — until then they are collected, not shown.
 *
 * Rooms are reported as room-nights: quantity = nights, price = average nightly
 * rate, so GA4 "items purchased" reads as room-nights sold. The event `value`
 * is always the exact stored total; item price is rounded to cents, so
 * price × quantity can differ from the room total by a few cents.
 */

export interface GaItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_category2?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
  index?: number;
}

export interface TrackPayload {
  ga?: { name: string; params: Record<string, unknown> };
  meta?: { name: string; params: Record<string, unknown> };
  /** Meta eventID (browser↔server dedup key). Booking/voucher invoice id. */
  eventId?: string;
}

export const ROOM_CATEGORY = 'Room';
export const VOUCHER_CATEGORY = 'Gift voucher';

export type PaymentType = 'card' | 'bank_transfer' | 'gift_voucher' | 'vacation_voucher';

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Normalise the many payment-method spellings we store/send ('Bank Transfer'
 * from the checkout button, 'bank_transfer' on vouchers, EuPlatesc's
 * 'Google Pay' / 'Credit/Debit Card' / 'Vacation Vouchers', …) into one of
 * four reportable values. Anything unrecognised went through the card gateway.
 */
export function paymentTypeFromMethod(method: string | null | undefined): PaymentType {
  const m = (method || '').toLowerCase().replace(/[\s_-]+/g, ' ').trim();
  if (m === 'bank transfer') return 'bank_transfer';
  if (m === 'gift voucher') return 'gift_voucher';
  if (m.includes('vacation')) return 'vacation_voucher';
  return 'card';
}

/** Whole days from `today` to `checkIn` (both YYYY-MM-DD). Never negative. */
export function leadTimeDays(checkIn: string, today: string): number | undefined {
  const a = Date.parse(`${checkIn}T00:00:00Z`);
  const b = Date.parse(`${today}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return undefined;
  return Math.max(0, Math.round((a - b) / 86_400_000));
}

/** Nights between two YYYY-MM-DD dates, or undefined when unusable. */
export function nightsBetween(checkIn: string | null | undefined, checkOut: string | null | undefined): number | undefined {
  if (!checkIn || !checkOut) return undefined;
  const n = leadTimeDays(checkOut, checkIn);
  return n && n > 0 ? n : undefined;
}

/** Today's date in the hotel's timezone, YYYY-MM-DD. */
export function hotelToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bucharest' }).format(now);
}

export interface RoomRef {
  slug: string;
  /** English room name (rooms.name). Falls back to the slug if missing. */
  nameEn: string | null | undefined;
  /** English room-type name (room_types.display_name). */
  typeEn?: string | null;
}

export function roomItem(
  room: RoomRef,
  opts: { total?: number | null; nights?: number | null; planEn?: string | null; index?: number } = {},
): GaItem {
  const item: GaItem = {
    item_id: room.slug,
    item_name: room.nameEn || room.slug,
    item_category: ROOM_CATEGORY,
  };
  if (room.typeEn) item.item_category2 = room.typeEn;
  if (opts.planEn) item.item_variant = opts.planEn;
  const total = Number(opts.total);
  const nights = Number(opts.nights);
  if (Number.isFinite(total) && total > 0) {
    if (Number.isFinite(nights) && nights > 0) {
      item.price = round2(total / nights);
      item.quantity = nights;
    } else {
      item.price = round2(total);
      item.quantity = 1;
    }
  }
  if (opts.index !== undefined) item.index = opts.index;
  return item;
}

/** Meta standard-event params derived from the GA4 items (ids = slugs). */
function metaCommerce(items: GaItem[], value?: number, currency?: string): Record<string, unknown> {
  const params: Record<string, unknown> = {
    content_ids: items.map((i) => i.item_id),
    content_type: 'product',
    contents: items.map((i) => ({ id: i.item_id, quantity: i.quantity ?? 1 })),
    num_items: items.length,
  };
  if (value !== undefined && currency) {
    params.value = value;
    params.currency = currency;
  }
  return params;
}

/** Drop undefined/null/'' so GA4 never receives empty parameters. */
function compact(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  );
}

// ---------------------------------------------------------------------------
// Rooms funnel
// ---------------------------------------------------------------------------

/** Rooms list rendered. GA4 only — Meta has no list-view standard event. */
export function buildViewItemList(rooms: RoomRef[]): TrackPayload {
  const items = rooms.map((r, index) => roomItem(r, { index }));
  return {
    ga: {
      name: 'view_item_list',
      params: { item_list_id: 'rooms', item_list_name: 'Rooms', items },
    },
  };
}

/** Room detail page rendered. */
export function buildViewItem(room: RoomRef): TrackPayload {
  const items = [roomItem(room)];
  return {
    ga: { name: 'view_item', params: { items } },
    meta: { name: 'ViewContent', params: metaCommerce(items) },
  };
}

/** A room was added to the selection ("Select" or "Book now"). */
export function buildAddToCart(
  room: RoomRef,
  dates: { checkIn?: string | null; checkOut?: string | null; today: string },
): TrackPayload {
  const items = [roomItem(room)];
  const nights = nightsBetween(dates.checkIn, dates.checkOut);
  return {
    ga: {
      name: 'add_to_cart',
      params: compact({
        items,
        nights,
        lead_time_days: dates.checkIn ? leadTimeDays(dates.checkIn, dates.today) : undefined,
      }),
    },
    meta: { name: 'AddToCart', params: metaCommerce(items) },
  };
}

export interface CheckoutRoomLine extends RoomRef {
  /** This room's stay total (after single/extras, before promo). */
  total?: number | null;
  adults?: number | null;
  children?: number | null;
}

export interface CheckoutSnapshot {
  rooms: CheckoutRoomLine[];
  checkIn: string;
  nights: number;
  /** Booking value the guest sees (after promo preview). */
  value: number;
  currency: string;
  planEn?: string | null;
  coupon?: string | null;
  today: string;
}

function checkoutParams(s: CheckoutSnapshot): { params: Record<string, unknown>; items: GaItem[]; value: number } {
  const items = s.rooms.map((r, index) =>
    roomItem(r, { total: r.total, nights: s.nights, planEn: s.planEn, index }),
  );
  const value = round2(s.value);
  const adults = s.rooms.reduce((sum, r) => sum + (Number(r.adults) || 0), 0);
  const children = s.rooms.reduce((sum, r) => sum + (Number(r.children) || 0), 0);
  const params = compact({
    currency: s.currency,
    value,
    coupon: s.coupon || undefined,
    items,
    nights: s.nights > 0 ? s.nights : undefined,
    lead_time_days: leadTimeDays(s.checkIn, s.today),
    adults: adults || undefined,
    children,
  });
  return { params, items, value };
}

/** Checkout page loaded with priced rooms. */
export function buildBeginCheckout(s: CheckoutSnapshot): TrackPayload {
  const { params, items, value } = checkoutParams(s);
  return {
    ga: { name: 'begin_checkout', params },
    meta: { name: 'InitiateCheckout', params: metaCommerce(items, value, s.currency) },
  };
}

/** Guest pressed a payment button and the form validated. */
export function buildAddPaymentInfo(s: CheckoutSnapshot, paymentType: PaymentType): TrackPayload {
  const { params, items, value } = checkoutParams(s);
  return {
    ga: { name: 'add_payment_info', params: { ...params, payment_type: paymentType } },
    meta: { name: 'AddPaymentInfo', params: metaCommerce(items, value, s.currency) },
  };
}

// ---------------------------------------------------------------------------
// Purchases (server-built from the stored booking / voucher row)
// ---------------------------------------------------------------------------

export interface BookingPurchaseInput {
  invoiceId: string;
  total: number;
  currency: string;
  checkIn: string;
  nights: number;
  paymentMethod: string | null;
  promoCode?: string | null;
  planEn?: string | null;
  rooms: CheckoutRoomLine[];
  today: string;
}

/**
 * A booking was placed: card payment approved, bank transfer submitted, or
 * gift-voucher booking recorded. `payment_type` tells them apart in reports.
 * transaction_id / eventID = the BK invoice id.
 */
export function buildBookingPurchase(b: BookingPurchaseInput): TrackPayload {
  const { params, items, value } = checkoutParams({
    rooms: b.rooms,
    checkIn: b.checkIn,
    nights: b.nights,
    value: b.total,
    currency: b.currency,
    planEn: b.planEn,
    coupon: b.promoCode,
    today: b.today,
  });
  return {
    ga: {
      name: 'purchase',
      params: {
        transaction_id: b.invoiceId,
        ...params,
        payment_type: paymentTypeFromMethod(b.paymentMethod),
      },
    },
    meta: { name: 'Purchase', params: metaCommerce(items, value, b.currency) },
    eventId: b.invoiceId,
  };
}

export type VoucherKind = 'value' | 'package' | 'spa_pass';

const VOUCHER_NAMES: Record<VoucherKind, string> = {
  value: 'Gift voucher (value)',
  package: 'Gift voucher (stay package)',
  spa_pass: 'Gift voucher (spa pass)',
};

export interface VoucherRef {
  type: VoucherKind;
  valueRon: number;
  /** Package vouchers: the room type they are for (English). */
  roomTypeEn?: string | null;
}

export function voucherItem(v: VoucherRef): GaItem {
  const item: GaItem = {
    item_id: `gift-voucher-${v.type}`,
    item_name: VOUCHER_NAMES[v.type] ?? `Gift voucher (${v.type})`,
    item_category: VOUCHER_CATEGORY,
    quantity: 1,
  };
  if (v.roomTypeEn) item.item_category2 = v.roomTypeEn;
  if (Number.isFinite(v.valueRon) && v.valueRon > 0) item.price = round2(v.valueRon);
  return item;
}

/** Voucher checkout page loaded with a draft. */
export function buildVoucherBeginCheckout(v: VoucherRef): TrackPayload {
  const items = [voucherItem(v)];
  const value = round2(v.valueRon);
  return {
    ga: { name: 'begin_checkout', params: { currency: 'RON', value, items } },
    meta: { name: 'InitiateCheckout', params: metaCommerce(items, value, 'RON') },
  };
}

/** A gift voucher was bought (card returned from gateway, or transfer chosen). */
export function buildVoucherPurchase(
  v: VoucherRef & { invoiceId: string; paymentMethod: string | null },
): TrackPayload {
  const items = [voucherItem(v)];
  const value = round2(v.valueRon);
  return {
    ga: {
      name: 'purchase',
      params: {
        transaction_id: v.invoiceId,
        currency: 'RON',
        value,
        items,
        payment_type: paymentTypeFromMethod(v.paymentMethod),
      },
    },
    meta: { name: 'Purchase', params: metaCommerce(items, value, 'RON') },
    eventId: v.invoiceId,
  };
}

// ---------------------------------------------------------------------------
// Reporting keys (offer ids, placements, lead sources)
// ---------------------------------------------------------------------------

/** GA4 truncates event / item parameter values beyond 100 characters. */
const GA_VALUE_MAX = 100;

/**
 * Normalise a reporting key (offer id, CTA placement, lead source) to
 * lowercase ASCII kebab/snake case, so a hand- or AI-written "Autumn 2026"
 * still reports as `autumn-2026` instead of opening a second GA4 row.
 * Existing ids pass through unchanged (`autumn-2026`, `new_years_eve`).
 * Returns null when nothing usable is left — the event is then not sent.
 */
export function toTrackingKey(raw: string | null | undefined): string | null {
  const key = (raw || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^[-_]+|[-_]+$/g, '')
    .slice(0, GA_VALUE_MAX);
  return key || null;
}

// ---------------------------------------------------------------------------
// Promotions (offer CTA clicks)
// ---------------------------------------------------------------------------

export const OFFER_CATEGORY = 'Offer';

export interface PromotionClick {
  /** `data-track-cta` — the offer id (its /offers/<slug>), the reporting key. */
  id: string | null | undefined;
  /** `data-track-cta-name` — human label; defaults to the id. */
  name?: string | null;
  /** `data-track-cta-slot` — placement on the site (hero, home-carousel, …). */
  slot?: string | null;
}

/**
 * A promotional call-to-action was clicked (see cta-click-tracker.tsx).
 * GA4 only: Meta ads optimise on Purchase and Meta logs button clicks itself.
 *
 * GA4's promotion dimensions (Item promotion ID / name / creative slot) are
 * item-scoped, so the promotion is also carried by one synthetic item
 * (`offer:<id>`, category Offer — appears only on this event, so it cannot
 * leak into room or voucher reports), with the promotion fields set on the
 * item itself as well as on the event.
 */
export function buildSelectPromotion(click: PromotionClick): TrackPayload | null {
  const id = toTrackingKey(click.id);
  if (!id) return null;
  const name = (click.name || '').trim().slice(0, GA_VALUE_MAX) || id;
  const promotion = compact({
    promotion_id: id,
    promotion_name: name,
    creative_slot: toTrackingKey(click.slot) ?? undefined,
  });
  const item: Record<string, unknown> = {
    item_id: `offer:${id}`,
    item_name: name,
    item_category: OFFER_CATEGORY,
    ...promotion,
  };
  return { ga: { name: 'select_promotion', params: { ...promotion, items: [item] } } };
}

// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------

/**
 * An inquiry form was sent successfully. `source` is the form: `corporate`,
 * `events`, `new_years_eve` (the NYE 2027 individual form — kept as is so
 * GA4 history does not split), or for a new offer form the offer id
 * (`buildLead('autumn-2026')`). Normalised by {@link toTrackingKey}.
 */
export function buildLead(source: string): TrackPayload | null {
  const key = toTrackingKey(source);
  if (!key) return null;
  return {
    ga: { name: 'generate_lead', params: { lead_source: key } },
    meta: { name: 'Lead', params: { content_name: key } },
  };
}
